import { Card, Icon, Tooltip } from "antd";
import { find, get } from "lodash";
import moment from "moment";
import React, { useMemo } from "react";
import { useLeadsContext } from "../../context/LeadsContext";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { LeadCard } from "../LeadCard";
import ColumnsSkeleton from "./ColumnsSkeleton";
import * as dashTasksActions from "~/_domains/societario/actions";
import { loadCustomers } from "~/_domains/customers/drawer/actions";
import { useDispatch } from "react-redux";
import { BASE_COLUMNS } from "../../constants";
import {
  Board,
  Column,
  ColumnContent,
  Container,
  ExtraBadge,
  ExtraContainer,
  ExtraTotalBadge,
  LoadingSkeleton,
} from "../../styles";

export const LeadColumns = () => {
  // Get all required data and handlers from context
  const {
    columns,
    originalColumns,
    moveLead,
    startShowingConfetti,
    date,
    isLoading,
    showDrawer,
    user,
    hideLeads
  } = useLeadsContext();

  const dispatch = useDispatch();

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }

    if (source.droppableId !== destination.droppableId) {
      if (parseInt(destination.droppableId) === 3) {
        startShowingConfetti();
      }

      await moveLead(
        get(find(originalColumns, { id: parseInt(source.droppableId) }), "leads", []),
        get(find(originalColumns, { id: parseInt(destination.droppableId) }), "leads", []),
        source,
        destination,
        result.draggableId
      );

      if (parseInt(destination.droppableId) === 4) {
        await dispatch(dashTasksActions.loadDashTasks({ showLoading: false }));
        await dispatch(loadCustomers({ showLoading: false }));
      }
        
    }
  };



  // Memoize expensive calculations
  const lastCreatedLead = useMemo(() => {
    if (!columns || !columns[0] || !columns[0].leads) return null;
    return [...columns[0].leads]
      .sort((a, b) => moment(b.created_at).diff(a.created_at))
      .at(0);
  }, [columns]);

  // Show skeleton loading when data is loading
  if (isLoading && (!columns || columns.length === 0)) {
    return <ColumnsSkeleton />;
  }

  // Memoize column calculations
  const baseColumns = useMemo(() => {
    return (columns && columns.length > 0) ? columns : BASE_COLUMNS;
  }, [columns]);
  
  // Use baseColumns directly since form leads now go to stage 1 automatically
  const displayColumns = useMemo(() => {
    return baseColumns;
  }, [baseColumns]);

  // Memoize sorted leads for each column to avoid sorting on every render
  const sortedColumnLeads = useMemo(() => {
    const sortedMap = new Map();
    displayColumns.forEach(column => {
      if (column.id === 1) {
        // Sort only for column 1 (first column)
        const sorted = (column.leads || []).sort((a, b) => 
          a.conversation_started - b.conversation_started
        );
        sortedMap.set(column.id, sorted);
      } else {
        // No sorting needed for other columns
        sortedMap.set(column.id, column.leads || []);
      }
    });
    return sortedMap;
  }, [displayColumns]);

  var COLUMN_TOOLTIPS = {
    1: 'Novos leads que entraram no funil e ainda não foram qualificados.',
    2: 'Leads qualificados com potencial real de se tornarem clientes.',
    5: 'Leads que precisam de acompanhamento futuro.',
    6: 'Leads em fase de negociação que podem fechar no futuro.',
    3: 'Leads que fecharam negócio e se tornaram clientes.',
    4: 'Leads que não avançaram e foram descartados.',
  };

  // Remove hideLeads.form logic as all leads now go to stage 1
  // Keep for backward compatibility but return empty set
  const hideLeadsFormMap = useMemo(() => {
    return new Set();
  }, []);

  return (
    <Board>
      <Container
        style={
          false
            ? { zIndex: 999, position: "relative" }
            : undefined
        }
      >
        <DragDropContext onDragEnd={onDragEnd}>
          {displayColumns.map((column) => (
            <Column key={column.id}>
              <Droppable droppableId={String(column.id)}>
                {(provided) => (
                  <Card
                    title={
                      COLUMN_TOOLTIPS[column.id]
                        ? <span>{column.name} <Tooltip title={COLUMN_TOOLTIPS[column.id]} placement="bottom"><Icon type="info-circle" style={{ fontSize: 12, opacity: 0.7, marginLeft: 4 }} /></Tooltip></span>
                        : column.name
                    }
                    bodyStyle={{ padding: 0, height: "100%" }}
                    headStyle={column.style}
                    style={{ height: "100%", display: "flex", flexDirection: "column" }}
                    extra={
                      <ExtraContainer>
                        <ExtraTotalBadge
                          count={(sortedColumnLeads.get(column.id) || []).length}
                          overflowCount={9999}
                        />
                      </ExtraContainer>
                    }
                  >
                    <ColumnContent ref={provided.innerRef} style={{
                      overflowY: isLoading ? "hidden" : "auto",
                    }}>
                        {isLoading
                          ? Array(6)
                              .fill(1)
                              .map((_, index) => (
                                <LoadingSkeleton key={index} />
                              ))
                          : (sortedColumnLeads.get(column.id) || [])
                              .map((item, index) => {
                                // Check if this lead is from form using the memoized Set
                                const isFromForm = hideLeadsFormMap.has(item.id);
                                
                                return (
                                  <LeadCard
                                    key={index}
                                    draggableId={String(item.id)}
                                    index={index}
                                    item={item}
                                    showDrawer={showDrawer}
                                    columnId={column.id}
                                    showOnboarding={
                                      false &&
                                      lastCreatedLead &&
                                      item.id === lastCreatedLead.id
                                    }
                                    isFromForm={isFromForm}
                                  />
                                );
                              })}
                        {provided.placeholder}
                    </ColumnContent>
                  </Card>
                )}
              </Droppable>
            </Column>
          ))}
        </DragDropContext>
      </Container>
    </Board>
  );
};
